import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { SubHeaderCategories } from './SubHeaderCategories';
import { DesktopToolbelt } from './DesktopToolbelt';
import { LeaderboardModal } from '../ui/modals/LeaderboardModal';
import { UserModal } from '../ui/modals/UserModal';
import { HelpModal } from '../ui/modals/HelpModal';
import { StatsModal } from '../ui/modals/StatsModal';
import { DiscussionModal } from '../ui/modals/DiscussionModal';
import { UpgradePromptModal } from '../ui/modals/UpgradePromptModal';
import { OnboardingSurveyGate } from '../OnboardingSurveyGate';
import { SubFooter } from './SubFooter';
import { cn } from '../../utils/cn';
import { isNativeApp } from '../../utils/platform';
import { useAuth } from '../../hooks/useAuth';
import { useLayoutVariant } from '../../hooks/useLayoutVariant';
import { useHasPendingSurvey } from '../../hooks/useHasPendingSurvey';
import { useCategoryStockData } from '../../hooks/useCategoryStockData';
import { StockCategoryFilter } from '../../types/stockCategories';
import { LayoutVariantType } from '../../types/layout';
import { LeaderboardProvider, useLeaderboardModal } from '../../contexts/LeaderboardContext';

interface LayoutProps {
  children: React.ReactNode;
  variant?: LayoutVariantType | 'auto';
  className?: string;
  currentCategory?: StockCategoryFilter;
  gameDate?: string;
  hideHeader?: boolean;
  hideSubHeader?: boolean;
  hideStreakAndTimer?: boolean;
  isGameComplete?: boolean;
  mobileTopBanner?: React.ReactNode;
  /** Whether the game has started (user clicked Play on the start overlay). Defaults to true. */
  hasGameStarted?: boolean;
  /** Show sub-footer with category navigation, previous days, and special thanks */
  showSubFooter?: boolean;
  /** Hide the back bar on non-game pages (e.g., home page) */
  hideBackBar?: boolean;
  /** Hide the footer (e.g., home page renders its own) */
  hideFooter?: boolean;
  /** Hide the desktop toolbelt on non-game pages */
  hideToolbelt?: boolean;
  /** Show minimal mobile menu (hide game-specific items) */
  minimal?: boolean;
}

interface LayoutHeaderProps {
  variant: LayoutVariantType;
  onHelpClick: () => void;
  onStatsClick: () => void;
  onLeaderboardClick: () => void;
  onUserClick: () => void;
  onPremiumClick: () => void;
  onDiscussionClick: () => void;
  isUserLoggedIn?: boolean;
  isGameComplete?: boolean;
  currentCategory?: StockCategoryFilter;
  gameDate?: string;
  hideSubHeader?: boolean;
  hideStreakAndTimer?: boolean;
  className?: string;
  /** Whether the game has started (user clicked Play on the start overlay). Defaults to true. */
  hasGameStarted?: boolean;
  /** Hide the back bar on non-game pages (e.g., home page) */
  hideBackBar?: boolean;
  /** Show minimal mobile menu (hide game-specific items) */
  minimal?: boolean;
}

interface LayoutContentProps {
  variant: LayoutVariantType;
  children: React.ReactNode;
  className?: string;
}

/**
 * Back navigation bar shown on non-game pages in place of SubHeaderCategories
 */
const BackBar: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="w-full bg-[hsl(var(--dashboard-main-background))] dark:bg-gray-800 border-b border-gray-300 dark:border-border">
      <div className="mx-auto max-w-[1920px] px-4 py-1">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-0.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Go back"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
      </div>
    </div>
  );
};

/**
 * Enhanced Layout Header component that supports full-width desktop mode
 * Includes SubHeaderCategories for desktop category navigation
 */
const LayoutHeader: React.FC<LayoutHeaderProps> = ({
  variant,
  onHelpClick,
  onStatsClick,
  onLeaderboardClick,
  onUserClick,
  onPremiumClick,
  onDiscussionClick,
  isUserLoggedIn = false,
  isGameComplete = false,
  currentCategory,
  gameDate,
  hideSubHeader = false,
  hideStreakAndTimer = false,
  className,
  hasGameStarted = true,
  hideBackBar = false,
  minimal = false
}) => {
  return (
    <header
      className={cn(
        "w-full bg-background",
        "sticky top-0 z-50",
        className
      )}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      role="banner"
    >
      {/* Main Header Row with max-width constraint */}
      <div className="border-b border-gray-300 dark:border-border">
        <div className="mx-auto max-w-[1920px] px-4 py-0.5">
          <Header
            onHelpClick={onHelpClick}
            onStatsClick={onStatsClick}
            onLeaderboardClick={onLeaderboardClick}
            onUserClick={onUserClick}
            onPremiumClick={onPremiumClick}
            onDiscussionClick={onDiscussionClick}
            isUserLoggedIn={isUserLoggedIn}
            isGameComplete={isGameComplete}
            category={currentCategory}
            gameDate={gameDate}
            hideStreakAndTimer={hideStreakAndTimer}
            hasGameStarted={hasGameStarted}
            minimal={minimal}
          />
        </div>
      </div>

      {/* Sub-Header: Categories on game pages, Back button on other pages */}
      {!hideSubHeader ? (
        <div className="w-full bg-[hsl(var(--dashboard-main-background))] dark:bg-gray-800 border-b border-gray-300 dark:border-border">
          <div className="mx-auto max-w-[1920px] px-4 py-1">
            <SubHeaderCategories currentCategory={currentCategory} />
          </div>
        </div>
      ) : !hideBackBar ? (
        <BackBar />
      ) : null}
    </header>
  );
};

/**
 * Layout Content wrapper component for variant-specific containers
 */
const LayoutContent: React.FC<LayoutContentProps> = ({
  variant,
  children,
  className
}) => {
  return (
    <div className={cn(
      // Let content size naturally so footer sits right below it (avoids tall empty gap)
      variant === 'desktop' ? "w-full" : "",
      className
    )}>
      {children}
    </div>
  );
};

const LayoutInner: React.FC<LayoutProps> = ({
  children,
  variant = 'auto',
  className,
  currentCategory,
  gameDate,
  hideHeader = false,
  hideSubHeader = false,
  hideStreakAndTimer = false,
  isGameComplete = false,
  mobileTopBanner,
  hasGameStarted = true,
  showSubFooter = false,
  hideBackBar = false,
  hideFooter = false,
  hideToolbelt = false,
  minimal = false
}) => {
  const { user } = useAuth();
  const detectedVariant = useLayoutVariant();
  const { isLeaderboardOpen, openLeaderboard, closeLeaderboard } = useLeaderboardModal();
  const [showUserModal, setShowUserModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showDiscussionModal, setShowDiscussionModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const hasPendingSurvey = useHasPendingSurvey();

  const activeVariant = variant === 'auto' ? detectedVariant.type : variant;
  const shouldCenterStreamerLayout = detectedVariant.isStreamerMode;
  const shouldApplyStreamerWrapper = shouldCenterStreamerLayout && activeVariant === 'mobile';
  const isUserLoggedIn = Boolean(user);

  // Fetch stock data only when discussion modal is opened (for stock symbol)
  const { stockData: dailyStock } = useCategoryStockData({
    category: currentCategory || 'popular',
    autoFetch: showDiscussionModal
  });

  // Listen for custom event to open UserModal for checkout flow
  useEffect(() => {
    const handleOpenUserModal = () => {
      setShowUserModal(true);
    };

    window.addEventListener('open-user-modal-for-checkout', handleOpenUserModal);
    return () => {
      window.removeEventListener('open-user-modal-for-checkout', handleOpenUserModal);
    };
  }, []);

  const shouldShowMobileBanner =
    !hideHeader && activeVariant === 'mobile' && Boolean(mobileTopBanner);

  const layoutContent = (
    <LayoutContent variant={activeVariant}>
      {children}
    </LayoutContent>
  );


  return (
    <div
      className={cn(
        "min-h-screen bg-background text-foreground transition-colors duration-300",
        "flex flex-col",
        activeVariant === 'desktop' ? "dashboard-layout" : "mobile-layout",
        className
      )}
      role="main"
      aria-label="Main application content"
    >
      {shouldShowMobileBanner && (
        <div className="w-full">
          {mobileTopBanner}
        </div>
      )}
      {!hideHeader && (
        <>
          <LayoutHeader
            variant={activeVariant}
            onHelpClick={() => setShowHelpModal(true)}
            onStatsClick={() => setShowStatsModal(true)}
            onLeaderboardClick={openLeaderboard}
            onUserClick={() => setShowUserModal(true)}
            onPremiumClick={() => setShowPremiumModal(true)}
            onDiscussionClick={() => setShowDiscussionModal(true)}
            isUserLoggedIn={isUserLoggedIn}
            isGameComplete={isGameComplete}
            currentCategory={currentCategory}
            gameDate={gameDate}
            hideSubHeader={hideSubHeader}
            hideStreakAndTimer={hideStreakAndTimer}
            hasGameStarted={hasGameStarted}
            hideBackBar={hideBackBar}
            minimal={minimal}
          />
          {!hideToolbelt && (
            <DesktopToolbelt
              onLeaderboardClick={openLeaderboard}
              onStatsClick={() => setShowStatsModal(true)}
              onDiscussionClick={() => setShowDiscussionModal(true)}
              onPremiumClick={() => setShowPremiumModal(true)}
              showMemberNotificationDot={hasPendingSurvey}
            />
          )}
        </>
      )}

      {shouldApplyStreamerWrapper ? (
        <div className="w-full max-w-[675px] mx-auto">
          {layoutContent}
        </div>
      ) : (
        layoutContent
      )}

      {/* Sub-Footer (game pages only) */}
      {showSubFooter && currentCategory && (
        <SubFooter currentCategory={currentCategory} />
      )}

      {/* Footer - hidden in native Capacitor app or when hideFooter is set */}
      {!hideFooter && !isNativeApp() && <Footer />}

      {/* Modals */}
      <LeaderboardModal
        isOpen={isLeaderboardOpen}
        onClose={closeLeaderboard}
        category={currentCategory || 'popular'}
        gameDate={gameDate}
        onSignInClick={() => setShowUserModal(true)}
      />

      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      <StatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        category={currentCategory}
        userId={user?.id}
        onSignInClick={() => setShowUserModal(true)}
      />

      <UserModal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
      />

      <DiscussionModal
        isOpen={showDiscussionModal}
        onClose={() => setShowDiscussionModal(false)}
        stockSymbol={dailyStock?.Symbol || null}
        category={currentCategory}
        gameDate={gameDate}
      />

      <UpgradePromptModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        requiresPremium={true}
        feature="Premium Membership"
      />

      <OnboardingSurveyGate />
    </div>
  );
};

export const Layout: React.FC<LayoutProps> = (props) => {
  return (
    <LeaderboardProvider>
      <LayoutInner {...props} />
    </LeaderboardProvider>
  );
};

export default Layout;
